/**
 * Process Control read/write client (docs/control/API.md). Types mirror the server payloads; nothing here invents a
 * field the API does not return. Every payload is for a simulated plant.
 */
import { api } from "./api.js";

export type Quality = "GOOD" | "UNCERTAIN" | "BAD";
export interface TagView {
  unit: string;
  tag: string;
  value: number | boolean;
  status: Quality;
  eu: string | null;
  /** URS-CTL-044: where the value comes from; an unbound I/O tag is "simulated", a derived one "calculated". */
  source?: "simulated" | "opcua" | "modbus" | "calculated";
  quality?: "GOOD" | "BAD" | "SIMULATED" | null;
}
export interface UnitIo {
  unit: string;
  mode: "SIMULATED" | "MIXED" | "HARDWARE";
  bound: string[];
  unbound: string[];
  devices: { kind: string; healthy: boolean; lastError: string | null }[];
}
export interface AlarmRow {
  unit: string;
  id: string;
  state: string;
  priority: number;
  type: string;
  limit: number | null;
  consequence: string | null;
  response: string | null;
  changedAt: string | null;
  awaitingAck: boolean;
}
export interface ClockInfo {
  mode: string;
  factor: number;
  runClass: string;
  gmpLinked: boolean;
  simulated: boolean;
  simTimeSeconds: number;
}
export interface ReleaseRef {
  id: string;
  no: number;
  hash: string;
  activatedAt: string;
}
export interface ControlSnapshot {
  state: string;
  release: ReleaseRef | null;
  clock: ClockInfo;
  tags: TagView[];
  alarms: AlarmRow[];
  /** Per unit: SIMULATED, MIXED (some signals bound to hardware) or HARDWARE. */
  io?: UnitIo[];
}
export interface ModuleStat {
  unit: string;
  module: string;
  periodMs: number;
  scans: number;
  avgMs: number;
  worstMs: number;
  overruns: number;
}
export interface ControlHealth {
  state: string;
  refusal: string | null;
  release: ReleaseRef | null;
  loop: { tickMs: number; lastTickAt: string | null; maxLagMs: number; stalls: number; clampedMs: number };
  clock: ClockInfo;
  modules: ModuleStat[];
  historian: {
    depth: number;
    capacity: number;
    written: number;
    dropped: number;
    totalDropped: number;
    rejected: number;
    lastError: string | null;
  };
  events: { queued: number; lastError: string | null };
}
export interface HistoryPoint {
  t: string;
  v: number;
  s: Quality;
  k: string;
}
export interface HistoryResult {
  simulated: boolean;
  training: boolean;
  truncated: boolean;
  series: Record<string, HistoryPoint[]>;
}
export interface CommandRow {
  id: string;
  label: string;
  unit: string;
  tag: string;
  value: number | boolean;
  permitted: boolean;
  blockedBy: { id: string; text: string }[];
  permissivesNow: { id: string; text: string; ok: boolean }[];
}
export interface SigningInfo {
  requestId: string;
  subjectType: "control_request" | "control_alarm_request";
  subjectId: string;
  streamId: string;
  meaning: "PERFORMED";
  state: unknown;
  expiresInMinutes: number;
}
export type WriteResult =
  | { applied: true; unit: string; tag: string; old: number | boolean; new: number | boolean }
  | { applied: false; signatureRequired: true; signing: SigningInfo };
export interface ModuleAction {
  id: string;
  at: string;
  tag: string;
  action: string;
  old: number | boolean | null;
  new: number | boolean | null;
  outcome: "APPLIED" | "REFUSED";
  refusalReason: string | null;
  reason: string | null;
  by: string;
  signed: boolean;
  training: boolean;
}
export interface HistorianTagSummary {
  tag: string;
  deadband: number;
  eu: string | null;
  heartbeatS: number;
  samples: number;
  oldest: string | null;
  newest: string | null;
  gaps: { start: string; end: string; reason: string }[];
}
/** The parts of a configuration release the screens read (units, modules, blocks, alarms, historian, commands). */
export interface ReleaseContentView {
  units: {
    id: string;
    modules: { id: string; blocks: Record<string, unknown>[]; alarms?: Record<string, unknown>[] }[];
    historian: { tag: string; deadband: number; eu?: string; heartbeatS?: number }[];
    signedWrites: string[];
    commands: { id: string; label: string; tag: string; value: number | boolean }[];
  }[];
}

const BASE = "/api/v1/control";

export const control = {
  snapshot: () => api.get<ControlSnapshot>(`${BASE}/snapshot`),
  health: () => api.get<ControlHealth>(`${BASE}/health`),
  alarms: (state: "active" | "shelved" | "all" = "all") =>
    api.get<AlarmRow[]>(`${BASE}/alarms?state=${state}`),
  acknowledge: (id: string) =>
    api.post<{ id: string }>(`${BASE}/alarms/${encodeURIComponent(id)}/acknowledge`),
  history: (tags: string[], from: Date, to: Date) =>
    api.get<HistoryResult>(
      `${BASE}/history?tags=${tags.map(encodeURIComponent).join(",")}&from=${from.toISOString()}&to=${to.toISOString()}&limit=20000`,
    ),
  release: (id: string) => api.get<{ id: string; content: ReleaseContentView }>(`${BASE}/releases/${id}`),
  commands: () => api.get<CommandRow[]>(`${BASE}/commands`),
  runCommand: (id: string) => api.post<WriteResult>(`${BASE}/commands/${encodeURIComponent(id)}/run`),
  write: (body: { tag: string; value: number | boolean; reason?: string }) =>
    api.post<WriteResult>(`${BASE}/writes`, body),
  sign: (requestId: string, body: { payloadHash: string; idToken: string }) =>
    api.post<WriteResult>(`${BASE}/requests/${requestId}/sign`, body),
  moduleActions: (block: string) =>
    api.get<ModuleAction[]>(`${BASE}/modules/${encodeURIComponent(block)}/actions?limit=15`),
  historian: () => api.get<HistorianTagSummary[]>(`${BASE}/historian`),
};
